import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Box, Button, Checkbox, CircularProgress, FormControlLabel, Stack, Typography} from '@mui/material'
import {useNavigate} from 'react-router-dom'
import {AppContext} from '../../contexts/AppContextProvider'
import useScreenFlow from '../../hooks/useScreenFlow'
import http from '../../http'
import prepareComplaintRequestData from '../../lib/property-mapper'
import useLogger from '../../hooks/useLogger'
import {OrgCustomizationContext} from '../../themes/OrgCustomizationProvider'
import {cancelComplaint, createSecondaryComplaint} from '../../contexts/useComplaintReducer/RootReducerFactory'
import {resetIndexedDb} from "../../lib/db";

const ComplaintCompletion: React.FC = () => {
    const navigate = useNavigate()
    const {screenPaths} = useScreenFlow()
    const {t, storeUrl} = useContext(OrgCustomizationContext)
    const {
        complaint,
        customer,
        shouldClearSessionAfterSubmission,
        dispatch,
        loggedIn,
        countryCode
    } = useContext(AppContext)
    const log = useLogger()
    const [inProgress, setInProgress] = useState(true)
    const [success, setSuccess] = useState(false)
    const [complaintId, setComplaintId] = useState('')
    const useSamePatientAndTreatmentDates = useRef<HTMLInputElement>(null)
    const hasSecondaryDeviceComplaint = useMemo(() => complaint.secondaryDevice.hasSecondaryDeviceComplaint === 'YES', [complaint.secondaryDevice.hasSecondaryDeviceComplaint])

    useEffect(() => {
        if (!complaint.generalComplaint.selectedProduct) return

        if (!complaint.guestInfo.customerNumber && !customer.customer) {
            loggedIn ? navigate(screenPaths.natureOfComplaint) : navigate(screenPaths.natureOfComplaintGuest)
        }
        
        if (complaintId || !inProgress) return

        if (sessionStorage.getItem('complaintSubmitted') === 'true') {
            setSuccess(true)
            setInProgress(false)
            setComplaintId(sessionStorage.getItem('complaintId') || '')
            // log that a duplicate complaint was attempted for the specified complaint ID
            log.error(new Error('Duplicate complaint (due to page refresh) for Complaint: ' + sessionStorage.getItem('complaintId') + ' prevented.'))
        } else {
            prepareComplaintRequestData(complaint).then(requestData => http.post(
                `/api/QualityNotification?countryCode=${countryCode}`,
                requestData
            )
            .then((response) => {
                log.email('logged from Complaint Completion')

                const result = JSON.parse(JSON.stringify(response))

                if (result.name !== 'Error') {
                    setSuccess(true)
                    setComplaintId(response.data.complaintId)
                    // to prevent duplicate submissions, we store a flag and the complaint id in session storage
                    sessionStorage.setItem('complaintSubmitted', 'true')
                    sessionStorage.setItem('complaintId', response.data.complaintId)
                }
            })
            .catch((error) => {
                log.email('logged from Complaint Completion')
                log.error(error)
            }))
            .finally(() => setInProgress(false))
        }
    }, [
        complaint,
        setInProgress,
        setSuccess,
        shouldClearSessionAfterSubmission,
        dispatch,
        complaintId,
        inProgress
    ])

    const clearSession = async (condition: boolean) => {
        if (condition) {
            await resetIndexedDb();
            dispatch(cancelComplaint())
            sessionStorage.clear()
        }
    }

    const goToHome = async () => {
        await clearSession(shouldClearSessionAfterSubmission)

        const redir = window.location

        const search = window.location.search

        redir.href = `${storeUrl}/${search}`
    }

    const fileAnotherComplaint = async () => {
        sessionStorage.clear()

        const navigatePath = hasSecondaryDeviceComplaint ? screenPaths.productType : screenPaths.productSearch

        await resetIndexedDb()

        dispatch(createSecondaryComplaint(hasSecondaryDeviceComplaint
            ? true
            : useSamePatientAndTreatmentDates?.current?.checked !== undefined
                ? useSamePatientAndTreatmentDates.current.checked
                : true
        ))

        navigate(navigatePath)
    }

    return !inProgress
        ? (
            <>
                {!success && <Typography variant='h5'>{t('cc_error_message')}</Typography>}
                {success && (
                    <>
                        <Typography variant='h5'>{t('cc_thank_you_heading')}</Typography>

                        <p>{t('cc_complaint_received')}</p>
                        <p>{t('cc_complaint_id', {complaintId})}</p>
                        <p>{t('cc_thank_you')}</p>
                        <p>{t('cc_customer_service')}</p>
                        <p>{t('cc_implant_systems')}</p>
                    </>
                )}

                <Stack direction='column' spacing={2}>
                    <Button variant='contained' color='primary' onClick={goToHome} sx={{width: '15rem'}}>
                        {t('btn_back_to_home')}
                    </Button>

                    <Stack direction='row' spacing={2}>
                        <Button variant='contained' color='primary' onClick={fileAnotherComplaint}
                                sx={{width: '15rem'}}>
                            {t(hasSecondaryDeviceComplaint ? 'cc_proceed_with_secondary_complaint' : 'btn_file_another_complaint')}
                        </Button>

                        {
                            (complaint.secondaryDevice.hasSecondaryDevice === 'NO' ||
                                complaint.secondaryDevice.hasSecondaryDeviceComplaint === 'NO') &&
                            <FormControlLabel label={t('cc_use_same_patient_and_treatment_dates')}
                                              control={<Checkbox inputRef={useSamePatientAndTreatmentDates}
                                                                 defaultChecked={false}/>}/>
                        }
                    </Stack>
                </Stack>
            </>
        )
        : (
            <>
                <Stack direction='row' alignItems='center' sx={{width: '100%', textAlign: 'center', mt: 10}}>
                    <Typography component='div' variant='h5' color='primary' sx={{width: '100%'}}>
                        Submitting your complaint
                    </Typography>
                </Stack>

                <Stack direction='row' alignContent='center' sx={{width: '100%', textAlign: 'center', mt: 5}}>
                    <Box sx={{width: '100%'}}>
                        <CircularProgress size='3rem'/>
                    </Box>
                </Stack>
            </>
        )
}

export default ComplaintCompletion
